// mrpProcessingModel.jsx
 
import axios from "axios";
 
 
const baseFolderPath = 'mrp-setting/';
const apiEndpoint = 'mrp-setting';
 
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const timeout = setTimeout(() => {
      reader.abort();
      reject(new Error("File reading timed out. The file may be too large."));
    }, 60000);
 
    reader.onload = () => {
      clearTimeout(timeout);
      try {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      } catch (error) {
        reject(new Error("Failed to process file as base64"));
      }
    };
 
    reader.onerror = error => {
      clearTimeout(timeout);
      reject(new Error("Failed to read file"));
    };
 
    reader.readAsDataURL(file);
  });
};
 
 
 
export const uploadAutomationData = async (
  selectedFiles,
  selectedTool,
  selectedModel,
  uniqueRecords,
  totalCount,
  folderId
) => {
  try {
    if (!selectedFiles || selectedFiles.length === 0) {
      throw new Error("No files selected");
    }
 
    const uploadResults = [];
    let apiResponse;
 
    const token = localStorage.getItem("token");
 
    if (!token) {
      throw new Error(
        "Authentication token not found. Please log in again."
      );
    }
 
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileExtension = file.name.split(".").pop();
 
      let base64String;
 
      try {
        base64String = await fileToBase64(file);
      } catch (error) {
        throw new Error(
          `Failed to convert file to base64: ${error.message}`
        );
      }
 
      const payload = {
        fileName: file.name,
        base64File: base64String,
        custom_batch_id: folderId,
        tool_name: selectedTool,
        model_name: selectedModel,
        unique_records: uniqueRecords,
        total_records: totalCount,
      };
 
      const apiUrl =
        `https://devai.conasservices.com/v1/uat_mrpseg/${apiEndpoint}/input_file`;
 
      try {
        apiResponse = await axios.post(
          apiUrl,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": token,
            },
            timeout: 120000,
          }
        );
      } catch (error) {
        if (error.code === "ECONNABORTED") {
          throw new Error(
            "API request timed out. The server may be busy, please try again later."
          );
        }
 
        throw error;
      }
 
      if (apiResponse && apiResponse.status === 201) {
        // The real backend processes the file directly from the base64
        // payload in this single call — there is no separate S3 upload
        // step, so no presigned URL is ever returned. batch_id is what
        // the process/download steps need to reference this upload.
        const { batch_id } = apiResponse.data;
 
        if (!batch_id) {
          throw new Error(
            "Backend did not return a batch_id for the uploaded file."
          );
        }
 
        uploadResults.push({
          originalFile: file.name,
          fileSize: file.size,
          fileType: file.type,
          folderId: batch_id || folderId,
          activityType: selectedTool,
        });
 
      } else {
        throw new Error(
          `File validation failed. API returned status ${
            apiResponse ? apiResponse.status : "unknown"
          }`
        );
      }
    }
 
    return {
      status: 201,
      data: uploadResults,
      apiResponse: apiResponse?.data,
    };
 
  } catch (error) {
    throw error;
  }
};
 
export const processAutomationData = async (
  selectedTool, data, selectedModel,
  totalCount, batch_id,
  limit = 100,
  offset = 0
) => {
  try {
    const token = localStorage.getItem('token');
    const payload = {
      ...data,
      model_name: selectedModel,
      reference_batch_id: batch_id,
      limit,
      offset,
    };
 
    const response = await axios.post(
      `https://devai.conasservices.com/v1/uat_mrpseg/${apiEndpoint}/process_records`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        },
        timeout: 28000
      }
    );
 
    return response.data;
  } catch (error) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};
 
/**
 * No-op. MRP Setting's real upload route never puts anything in S3 — the
 * file is submitted as base64 and processed directly in one call — so
 * there's nothing here to delete. Kept as an exported function (rather
 * than removed outright) since other code may still call it; safe to
 * leave in place indefinitely, or remove the call site entirely once
 * confirmed nothing references it.
 */
export const checkAndDeleteFileFromS3 = async () => {
  return {
    status: 'success',
    message: 'No S3 cleanup needed — MRP Setting does not store files in S3.',
    skipped: true,
  };
};
 
export const checkCountData = async (data, selectedTool, selectedModel) => {
  try {
    const token = localStorage.getItem('token');
 
    const payload = {
      ...data,
      model_name: selectedModel
    };
 
    const response = await axios.post(
      `https://devai.conasservices.com/v1/uat_mrpseg/${apiEndpoint}/input_check`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        }
      }
    );
 
    if (response === undefined) {
      throw new Error("No response");
    }
 
    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) {
        throw new Error(response.data.message);
      } else {
        return response.data;
      }
    }
  } catch (error) {
    let sendError = error;
    if (error.response?.data?.message) {
      sendError = new Error(error.response.data.message);
    } else if (error.response?.data?.detail) {
      sendError = new Error(error.response.data.detail);
    } else {
      sendError = new Error(
        "Something unexpected happened. " +
        "If the issue persists, please contact our support team for assistance."
      );
    }
    throw sendError;
  }
};
 
 
export const downloadProcessedData = async (
  selectedTool, batchId, model, isTranslated = false
) => {
  if (!batchId) throw new Error('Batch ID is required.');
 
  const apiKey = localStorage.getItem('token');
  if (!apiKey) throw new Error('API key not found. Please log in again.');
 
  const url = `https://devai.conasservices.com/v1/uat_mrpseg/${apiEndpoint}/output_file`
    + `?batch_id=${encodeURIComponent(batchId)}`;
 
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey
    }
  });
 
  if (!response.ok) {
    throw new Error(`Server responded with status: ${response.status}`);
  }
 
  // The real backend returns the file directly as base64 text in the
  // response body (with the filename in an X-Filename header) — there is
  // no download_url to fetch a second time from S3.
  //
  // Note: reading a custom response header like X-Filename across origins
  // (e.g. frontend on :3000, backend on :8000) requires the backend to
  // send Access-Control-Expose-Headers: X-Filename — otherwise the browser
  // hides it from JS even though it's in the response. The fallback name
  // below keeps this working either way; ask on the backend side if you
  // want the real timestamped filename to come through instead.
  const filename =
    response.headers.get('X-Filename') || `mrp_setting_results_${batchId}.xlsx`;
  const base64Data = await response.text();
 
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
 
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
 
  setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
 
  return { filename };
};
 
export const gethistoryData = async (selectedTool) => {
  try {
    const token = localStorage.getItem('token');
 
    const response = await axios.get(
      `https://devai.conasservices.com/v1/uat_mrpseg/${apiEndpoint}/operation_history`,
      {
        params: {
          tool_name: selectedTool
        },
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        }
      }
    );
 
    if (response === undefined) {
      throw new Error("No response");
    }
 
    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) {
        throw new Error(response.data.message);
      } else {
        return response.data;
      }
    }
  } catch (error) {
    let sendError = error;
    if (error.response?.data?.message) {
      sendError = new Error(error.response.data.message);
    } else if (error.response?.data?.detail) {
      sendError = new Error(error.response.data.detail);
    } else {
      sendError = new Error(
        "Something unexpected happened. " +
        "If the issue persists, please contact our support team for assistance."
      );
    }
    throw sendError;
  }
};
 
export const downloadInputFile = async (selectedTool, batchId) => {
  try {
    if (!batchId) {
      throw new Error('Batch ID is required for downloading input file.');
    }
 
    const apiKey = localStorage.getItem('token');
    if (!apiKey) {
      throw new Error('API key not found in local storage. Please log in again.');
    }
 
    const url = `https://devai.conasservices.com/v1/uat_mrpseg/${apiEndpoint}/input_download?batch_id=${encodeURIComponent(batchId)}`;
 
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey
      }
    });
 
    if (!response.ok) {
      throw new Error(`Server responded with status: ${response.status}`);
    }
 
    const base64Data = await response.text();
 
    return { base64Data };
  } catch (error) {
    throw error;
  }
};
 
// ============================================================================
// Chunked processing for the real MRP Setting backend (/mrp_setting_process).
// Adapted from Mpn Segregation's processAutomationDataWithChunking — kept
// the frontend-driven for-loop and resilience to individual chunk failures
// without aborting the batch. Differs because: (1) this backend needs one
// extra "initiate" call first to learn total_chunks/chunk_size, since it
// counts real rows server-side rather than trusting a client-supplied
// count; (2) failed chunks are retried a couple of times before being
// given up on, rather than skipped outright, so a transient failure
// doesn't silently leave materials without MRP settings; (3) the payload
// is just { batch_id, chunk_index } since this backend paginates
// server-side, no need to send startRecord/endRecord/chunkSize.
//
// Kept as a distinctly-named export (not reusing processAutomationData's
// name above) since its signature is different — (batchId, onProgress)
// rather than (selectedTool, data, selectedModel, totalCount, batch_id) —
// and to avoid silently changing behavior for any other caller of the
// existing processAutomationData.
// ============================================================================
 
const MRP_API_BASE = "https://devai.conasservices.com/v1/uat_mrpseg/mrp-setting";
const MAX_RETRIES_PER_CHUNK = 2;
const RETRY_DELAY_MS = 1500;
 
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
 
const postToMrpProcessRoute = (payload) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Authentication token not found. Please log in again.");
  }
  return axios.post(`${MRP_API_BASE}/process_records`, payload, {
    headers: { "Content-Type": "application/json", "x-api-key": token },
    timeout: 28000, // stay under API Gateway's 29s hard limit per call
  });
};
 
const processMrpChunkWithRetry = async (batchId, chunkIndex) => {
  let lastError;
  for (let attempt = 1; attempt <= MAX_RETRIES_PER_CHUNK; attempt++) {
    try {
      const response = await postToMrpProcessRoute({ batch_id: batchId, chunk_index: chunkIndex });
      return response.data;
    } catch (error) {
      lastError = error;
      console.error(`Chunk ${chunkIndex} attempt ${attempt}/${MAX_RETRIES_PER_CHUNK} failed:`, error.message);
      if (attempt < MAX_RETRIES_PER_CHUNK) {
        await sleep(RETRY_DELAY_MS);
      }
    }
  }
  throw lastError;
};
 
/**
 * Processes an already-uploaded MRP Setting batch end to end: one call to
 * initiate (learns total_chunks/chunk_size from the server), then loops
 * chunk_index 0..total_chunks-1 against the same endpoint.
 *
 * @param {string} batchId - the batch_id returned by the upload step
 * @param {(progress: object) => void} [onProgress] - called after every
 *   chunk (success or final failure) with:
 *   { chunkIndex, totalChunks, percentComplete, recordsSaved, recordsFailed, status }
 * @returns {Promise<object>} summary of the run
 */
export const processMrpSettingBatch = async (batchId, onProgress) => {
  if (!batchId) throw new Error("batch_id is required");
 
  const reportProgress = (progress) => {
    if (typeof onProgress === "function") onProgress(progress);
  };
 
  let totalChunks;
  let totalRecords;
  try {
    const initiateResponse = await postToMrpProcessRoute({ batch_id: batchId });
    totalChunks = initiateResponse.data.total_chunks;
    totalRecords = initiateResponse.data.total_records;
  } catch (error) {
    const detail = error.response?.data?.detail;
    throw new Error(detail || `Failed to initiate processing: ${error.message}`);
  }
 
  if (!totalChunks || totalChunks === 0) {
    throw new Error("No records found to process for this batch.");
  }
 
  console.log(`Processing batch_id=${batchId}: ${totalRecords} records across ${totalChunks} chunks`);
 
  let totalSaved = 0;
  let totalFailed = 0;
  const failedChunkIndexes = [];
  let lastChunkResult = null;
 
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    try {
      const chunkResult = await processMrpChunkWithRetry(batchId, chunkIndex);
      lastChunkResult = chunkResult;
      totalSaved += chunkResult.records_saved || 0;
      totalFailed += chunkResult.records_failed || 0;
 
      reportProgress({
        chunkIndex,
        totalChunks,
        percentComplete: chunkResult.progress_percent,
        recordsSaved: chunkResult.records_saved,
        recordsFailed: chunkResult.records_failed,
        status: "chunk_completed",
      });
    } catch (error) {
      console.error(`Chunk ${chunkIndex} failed after ${MAX_RETRIES_PER_CHUNK} attempts:`, error.message);
      failedChunkIndexes.push(chunkIndex);
 
      reportProgress({
        chunkIndex,
        totalChunks,
        percentComplete: Math.round(((chunkIndex + 1) / totalChunks) * 1000) / 10,
        status: "chunk_failed",
        error: error.message,
      });
    }
  }
 
  const isFullyComplete = failedChunkIndexes.length === 0;
 
  return {
    status: isFullyComplete ? "completed" : "completed_with_errors",
    batch_id: batchId,
    total_records: totalRecords,
    total_chunks: totalChunks,
    records_saved: totalSaved,
    records_failed_in_chunks: totalFailed,
    failed_chunk_indexes: failedChunkIndexes,
    confidence_summary: lastChunkResult?.confidence_summary || null,
    recommend_ai_review_count: lastChunkResult?.recommend_ai_review_count ?? null,
  };
};
 

export const retryFailedChunks = async (batchId, failedChunkIndexes, onProgress) => {
  const results = [];
  for (const chunkIndex of failedChunkIndexes) {
    try {
      const chunkResult = await processMrpChunkWithRetry(batchId, chunkIndex);
      results.push({ chunkIndex, status: "recovered", result: chunkResult });
      if (typeof onProgress === "function") onProgress({ chunkIndex, status: "chunk_recovered" });
    } catch (error) {
      results.push({ chunkIndex, status: "still_failing", error: error.message });
    }
  }
  return results;
};
 
 